"use client";

import { createClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";
import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import "@algolia/autocomplete-theme-classic"; // optional base styles
import { SearchIcon } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import ZeekMapsIcon from "./icons/ZeekMapsIcon";
import { TooltipContent } from "./tooltip-content";

const SEARCH_RESULT_LIMIT = 10;
const RECENT_SEARCHES_LIMIT = 5;

interface SearchBoxProps {
	className?: string;
	defaultValue?: string;
}

export default function SearchBox({
	className,
	defaultValue = "",
}: SearchBoxProps) {
	const supabase = createClient();
	const containerRef = useRef<HTMLDivElement>(null);
	const router = useRouter();
	const searchParams = useSearchParams();

	useEffect(() => {
		if (!containerRef.current) return;

		// Recent searches plugin
		const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
			key: "search", // localStorage key
			limit: RECENT_SEARCHES_LIMIT, // number of saved searches
		});

		const search = autocomplete({
			container: containerRef.current,
			placeholder: "Search anything …",
			// openOnFocus: true,
			initialState: {
				query: searchParams.get("q") || defaultValue || "",
			},
			plugins: [recentSearchesPlugin],
			detachedMediaQuery: "none", // keep inline on mobile
			// onSubmit({ state }) {
			// 	if (state.query.trim()) {
			// 		const params = new URLSearchParams(searchParams.toString());
			// 		params.set("q", state.query.trim());
			// 		router.push(`/blog?${params.toString()}`);
			// 	}
			// },
			onReset() {
				const params = new URLSearchParams(searchParams.toString());
				params.delete("q");
				router.push(`/blog?${params.toString()}`);
			},
			classNames: {
				form: "w-full bg-transparent border-0 rounded-full",
				input: "w-full h-[12px] bg-transparent focus:outline-none",
				submitButton: "", // hide default
				clearButton: "hidden", // hide default
				panel:
					"z-50 rounded-xl border border-gray-200 bg-background shadow-lg overflow-hidden",
				item: "cursor-pointer px-3 py-2 bg-background hover:bg-primary/10",
			},
			getSources({ query }) {
				if (!query) return [];
				return [
					{
						sourceId: "blogPosts",
						getItems: async () => {
							const { data, error } = await supabase
								.from("blog_posts")
								.select("id, title, slug")
								.ilike("title", `%${query}%`) // case-insensitive search
								.limit(SEARCH_RESULT_LIMIT);
							if (error) {
								console.error(error);
								return [];
							}
							return data ?? [];
						},
						getItemUrl({ item }) {
							return `/blog/${item.slug}`;
						},
						templates: {
							item({ item, components, html }) {
								return html`
                                    <a href="/blog/${item.slug}" class="block px-3 py-2 hover:bg-gray-100 rounded-md">
                                        <div class="font-medium">${item.title}</div>
                                    </a>
                                `;
							},
							noResults({ html }) {
								return html`
                                    <div class="px-3 py-2 text-gray-500">
                                        No posts found
                                    </div>
                                `;
							},
						},
					},
				];
			},
		});

		return () => search.destroy();
	}, [defaultValue, router, searchParams, supabase]);

	return (
		<div
			ref={containerRef}
			className={cn("w-full", className)}
			data-autofocus
		/>
	);
}

export function SearchInline() {
	const [showSearch, setShowSearch] = useState(false);

	return (
		<div
			className={cn(
				"flex flex-row gap-1 items-center justify-center transition-all",
			)}
		>
			{showSearch ? (
				<SearchBox />
			) : (
				<TooltipContent content="Search anything">
					<button
						type="button"
						className="flex items-center justify-center rounded-full hover:bg-primary/20 p-2"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setShowSearch((prev) => {
								return !prev;
							});
						}}
					>
						<SearchIcon className="size-5 text-primary" />
					</button>
				</TooltipContent>
			)}
		</div>
	);
}

export function SearchDialog() {
	const [showSearch, setShowSearch] = useState(false);
	return (
		<Modal
			opened={showSearch}
			onClose={() => setShowSearch(false)}
			title="Search"
			description="Our latest blog posts, pages & our documentation"
			closeable={false}
			logo={<ZeekMapsIcon className="size-12 text-primary self-center" />}
			className="bg-background/60 backdrop-blur-sm rounded-sm md:rounded-2xl text-center text-white border-none shadow-2xl pt-8"
			trigger={
				<div>
					<Button
						type="button"
						variant="ghost"
						className="flex items-center justify-center rounded-full p-2 hover:bg-primary/20"
						onClick={() => setShowSearch(true)}
					>
						<SearchIcon className="size-5 text-primary" />
					</Button>
				</div>
			}
		>
			<Card className="w-full bg-transparent border-none shadow-none">
				<CardContent>
					<div className="flex flex-col w-full items-center rounded-full bg-background p-3 border border-primary-34">
						<SearchBox />
					</div>
				</CardContent>
			</Card>
		</Modal>
	);
}
